import React from "react";
import { usePayment, useTheme } from "../hooks";
import { ArrowRight } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CorporateService } from "../models/Service";
import { usePayMutation } from "../api/queries/usePayMutation";
import { getStorageToken, isErrorResponse } from "../utils";
import { usePriceMutation } from "../api/queries";
import { useGlobalContext } from "../context/globalContext";
import { getErrorMessage } from "../utils/getErrorMessage";
import { emailRegex } from "../constants";

const PaymentActions = ({ service }: { service: CorporateService }) => {
  const { t } = useTranslation();
  const token = getStorageToken();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const payMutation = usePayMutation({ authorisation: token! });
  const priceMutation = usePriceMutation({ authorisation: token! });
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const {
    currentStep,
    setCurrentStep,
    amount,
    name,
    email,
    selectedMethod,
    phoneNumber,
    selectedWallet,
    customerUser,
    setError,
  } = usePayment();
  const { setTotalAmount, setTotalFees } = useGlobalContext();

  const isLoading = payMutation.isLoading || priceMutation.isLoading;

  const isStepOneValid = () => {
    return (
      amount.trim() !== "" &&
      parseInt(amount) >= 100 &&
      (!service.is_customer_search_enabled
        ? name.trim() !== "" &&
          email.trim() !== "" &&
          emailRegex.test(email.trim())
        : true) &&
      selectedMethod !== null &&
      phoneNumber.trim() !== "" &&
      phoneNumber.length === 9
    );
  };

  const handleContinue = () => {
    const basePayload = {
      amount: Number(amount),
      corporate_subsidiary_id: service?.corporate_subsidiary_id,
      payment_method_id: selectedMethod?.id!,
      corporate_service_id: service?.id,
    };

    switch (currentStep) {
      case 1: {
        if (!isStepOneValid()) return;

        if (!service?.is_customer_search_enabled) {
          priceMutation.mutate(basePayload, {
            onSuccess: data => {
              if (isErrorResponse(data)) {
                setError(getErrorMessage(data, currentLanguage));
                return;
              }
              setTotalFees(data.total_fees.toString());
              setTotalAmount(data.total_amount.toString());
              setCurrentStep(2);
            },
            onError: (err: any) => {
              const errMsg = isErrorResponse(err?.body)
                ? getErrorMessage(err.body, currentLanguage)
                : err?.message || t("errors.unknown_error");
              setError(errMsg);
            },
          });
        }

        break;
      }

      case 2: {
        if (!service?.is_customer_search_enabled) {
          payMutation.mutate(
            {
              ...basePayload,
              channel: "App",
              instrument_account_owner: name,
              instrument_account_number: phoneNumber,
              notification_email: email,
            },
            {
              onSuccess: data => {
                if (isErrorResponse(data)) {
                  setError(getErrorMessage(data, currentLanguage));
                }
              },
              onError: (err: any) => {
                const errMsg = isErrorResponse(err?.body)
                  ? getErrorMessage(err.body, currentLanguage)
                  : err?.message || t("errors.unknown_error");
                setError(errMsg);
              },
            }
          );
          return;
        }
        priceMutation.mutate(basePayload, {
          onSuccess: data => {
            if (isErrorResponse(data)) {
              setError(getErrorMessage(data, currentLanguage));
              return;
            }
            setTotalFees(data.total_fees.toString());
            setTotalAmount(data.total_amount.toString());
            setCurrentStep(3);
          },
          onError: (err: any) => {
            const errMsg = isErrorResponse(err?.body)
              ? getErrorMessage(err.body, currentLanguage)
              : err?.message || t("errors.unknown_error");
            setError(errMsg);
          },
        });
        break;
      }

      case 3: {
        payMutation.mutate(
          {
            ...basePayload,
            customer_id: customerUser?.id,
            customer_wallet_id: selectedWallet?.id,
            instrument_account_number: phoneNumber || undefined,
            channel: "App",
          },
          {
            onSuccess: data => {
              if (isErrorResponse(data)) {
                setError(getErrorMessage(data, currentLanguage));
              }
            },
            onError: (err: any) => {
              const errMsg = isErrorResponse(err?.body)
                ? getErrorMessage(err.body, currentLanguage)
                : err?.message || t("errors.unknown_error");
              setError(errMsg);
            },
          }
        );
        break;
      }

      default:
        break;
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: "white",
        border: 1,
        borderColor: theme?.color_primary || "grey.300",
        borderRadius: 2,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        p: 2,
        mb: 3,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      <Button
        onClick={() => navigate(-1)}
        variant="outlined"
        disabled={isLoading}
        sx={{
          px: 3,
          py: 1.5,
          fontWeight: 500,
          textTransform: "none",
          borderColor: "red",
          color: "red",
        }}
      >
        {t("payment.actions.cancel", "Retour")}
      </Button>

      <Box ml={currentStep === 1 ? "auto" : undefined}>
        {currentStep > 1 && (
          <Button
            onClick={handleBack}
            variant="outlined"
            disabled={isLoading}
            sx={{
              px: 3,
              py: 1.5,
              fontWeight: 500,
              textTransform: "none",
              borderColor: theme?.color_primary,
              color: theme?.color_primary,
              mr: 3,
            }}
          >
            {t("payment.actions.back", "Retour")}
          </Button>
        )}
        <Button
          onClick={handleContinue}
          disabled={
            ((currentStep === 2 || currentStep === 1) && !isStepOneValid()) ||
            isLoading
          }
          variant="contained"
          sx={{
            bgcolor: isStepOneValid() ? theme?.color_primary : "warning.main",
            color: "#fff",
            "&:hover": {
              bgcolor: isStepOneValid() ? theme?.color_primary : "warning.dark",
            },
            px: 3,
            py: 1.5,
            fontWeight: 500,
            textTransform: "none",
          }}
        >
          {isLoading ? (
            t("payment.actions.processing", "Traitement...")
          ) : (
            <>
              {currentStep === 1 ||
              (service?.is_customer_search_enabled && currentStep === 2)
                ? t("payment.actions.continue", "Continuer")
                : t("payment.actions.confirm", "Confirmer le paiement")}
              <ArrowRight sx={{ ml: 1, fontSize: 20 }} />
            </>
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentActions;

